import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { BookOpen, X, Sparkle } from '@phosphor-icons/react';

const TECHNIQUES = [
  { id: 'feynman', name: 'Feynman Technique', desc: 'Learn by explaining concepts simply.' },
  { id: 'pomodoro', name: 'Pomodoro Technique', desc: 'Time-boxed study intervals for high focus.' },
  { id: 'spaced-repetition', name: 'Spaced Repetition', desc: 'Strategic review to lock concepts into long-term memory.' },
  { id: 'active-recall', name: 'Active Recall', desc: 'Test yourself actively instead of passive reading.' },
  { id: 'sq3r', name: 'SQ3R Method', desc: 'Survey, Question, Read, Recite, Review.' },
  { id: 'mind-mapping', name: 'Mind Mapping', desc: 'Visual organization of complex syllabus structures.' },
];

export default function StudyGuides() {
  const { user, API } = useAuth();
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(false);

  const hasKey = !!user?.ai_api_key;

  const handleSelect = async (tech) => {
    setSelectedGuide(tech);
    setMarkdown('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/dashboard/guide`, { technique: tech.name }, { withCredentials: true });
      setMarkdown(data.markdown);
    } catch (err) {
      setMarkdown("Failed to fetch guide. Ensure your API Key is configured.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-8 max-w-5xl space-y-8" data-testid="study-guides-page">
      <div className="flex items-center gap-4 mb-8">
        <BookOpen size={48} weight="duotone" className="text-primary" />
        <div>
          <h1 className="text-4xl font-heading text-white uppercase tracking-widest">EXPERT STUDY GUIDES</h1>
          <p className="text-muted-foreground font-bold tracking-widest text-sm uppercase">AI-Tailored specific tricks and techniques for {user?.target_exam || 'your exam'}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {TECHNIQUES.map((tech) => (
          <Card key={tech.id} className="rounded-none border-border bg-card hover:border-primary/50 transition-colors cursor-pointer" onClick={() => handleSelect(tech)}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-xl uppercase tracking-widest text-white">{tech.name}</h3>
                <Sparkle size={20} className="text-chart-4" />
              </div>
              <p className="text-sm text-muted-foreground">{tech.desc}</p>
              <Button disabled={!hasKey} variant="ghost" className="mt-4 p-0 font-bold uppercase tracking-widest text-xs text-primary hover:text-primary hover:bg-transparent">
                {hasKey ? 'GENERATE GUIDE' : 'API KEY REQUIRED'}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {selectedGuide && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4">
          <Card className="w-full max-w-3xl max-h-[80vh] flex flex-col rounded-none border border-primary/50 bg-black/90 shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-white/10 bg-primary/10">
              <h2 className="font-heading text-2xl uppercase tracking-widest text-white flex items-center gap-2">
                <Sparkle className="text-primary"/> {selectedGuide.name} FOR {user?.target_exam}
              </h2>
              <button onClick={() => setSelectedGuide(null)} className="text-muted-foreground hover:text-white transition-colors">
                <X size={24} />
              </button>
            </div>
            <div className="p-6 overflow-y-auto custom-scrollbar">
              {loading ? (
                <div className="flex flex-col items-center justify-center py-12 text-primary animate-pulse space-y-4">
                  <Sparkle size={48} weight="duotone" />
                  <p className="font-bold tracking-widest uppercase">Consulting AI Knowledge Base...</p>
                </div>
              ) : (
                <div className="prose prose-invert prose-primary max-w-none font-body text-sm md:text-base">
                  {markdown.split('\n').map((line, i) => {
                    if (line.startsWith('### ')) return <h3 key={i} className="text-lg font-heading tracking-widest uppercase text-white mt-6 mb-2">{line.replace('### ', '')}</h3>;
                    if (line.startsWith('## ')) return <h2 key={i} className="text-xl font-heading tracking-widest uppercase text-primary mt-8 mb-3">{line.replace('## ', '')}</h2>;
                    if (line.startsWith('# ')) return <h1 key={i} className="text-2xl font-heading tracking-widest uppercase text-white mt-8 mb-4">{line.replace('# ', '')}</h1>;
                    if (line.startsWith('- ') || line.startsWith('* ')) return <li key={i} className="ml-4 mb-1">{line.substring(2)}</li>;
                    if (line.trim() === '') return <br key={i}/>;
                    
                    // Simple bold parsing
                    const formattedLine = line.split(/(\*\*.*?\*\*)/).map((part, idx) => {
                      if (part.startsWith('**') && part.endsWith('**')) {
                        return <strong key={idx} className="text-white">{part.slice(2, -2)}</strong>;
                      }
                      return part;
                    });
                    
                    return <p key={i} className="mb-2 text-muted-foreground leading-relaxed">{formattedLine}</p>;
                  })}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}